import axios from "axios";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import type {OverLimitAllowance, OverLimitOffer, OverLimitPricing} from "./overLimitOffer";
import type {FileListFacts} from "./fileListRules";

/**
 * The over-limit endpoints the composer still reads (api kitecorp/transferify-api-service#823): the quote, whose plan
 * rows the walls offer, and the settled purchase that still pays for a send. The one-off's checkout is not called any
 * more: the api sells no one-off since plan W2 (kitecorp/transferify-ui#3916).
 *
 * Every call is authenticated, and that follows from the purchase rather than being a separate policy:
 * an allowance belongs to an account. Which BRAND — and therefore which currency — a call concerns is
 * resolved by the api from the trusted `Origin`/`Referer`, never from a body field, so a sender buys on
 * the site they are on and is never quoted a converted figure. Nothing here sends a brand or a price.
 *
 * No route flag is sent. Every transfer is encrypted, so the api prices every request against one sale
 * ceiling and ignores the deprecated `encrypted` field (kitecorp/transferify-api-service#2458). An api
 * older than that read an absent flag as the lower plaintext ceiling, which is why this ships only with a
 * schema snapshot synced from an api that has the change: the prod deploy gate (scripts/apiRouteGate.mjs)
 * then holds the bundle until prod runs that build.
 *
 * `instant` IS sent on EVERY call, on both paths, and always explicitly (api#1039). The api reads an
 * absent value as HOSTED, which is what every deployed client means by omitting it — so omitting it here
 * on the hosted path would make "we decided hosted" and "we forgot to say" the same request. The quote
 * answers for the path. The allowance read needs it as much: a purchase pays for a send of either type for
 * what was paid (kitecorp/transferify-api-service#2787), so EUR 1.99 pays for any instant send but only a
 * 40 GB hosted one, and the read answers for the path asked about.
 *
 * The selection's FILE-LIST FACTS ride the quote (./fileListRules, api kitecorp/transferify-api-service#2144):
 * the api refuses, before any plan is offered, a selection the upload init would refuse. A REQUIRED parameter,
 * deliberately: the api reads their absence as "not checked", so a call that forgot them here would offer a plan
 * for a send the init then refuses.
 */

/**
 * How long these calls wait before giving up.
 *
 * EVERY CALL HERE NEEDS ONE. Axios defaults to no timeout and this app sets no global default, so a
 * stalled connection never settles — and a quote that never settles is not a failure the surface can
 * answer: it leaves the strip on "working out the price…" with no control and the Send disabled, which
 * is the ui#1715 defect reached from a hang instead of a 401. A timeout turns every hang into the
 * rejection the hook already handles.
 *
 * Short because the quote re-asks: four attempts at this bound is the worst the sender waits before being told.
 */
const READ_TIMEOUT_MS = 15_000;

/*
 * Compile-time bindings to the generated spec (api/schemaAssert.ts). A backend rename, retype or
 * nullability change fails `tsc -b` naming the field, instead of surfacing as an undefined at runtime.
 */
/*
 * `credit` is exempt because this ui ships ahead of the snapshot for that one field: the api publishes it since
 * kitecorp/transferify-api-service#2787 (`OverLimitResponses.Offer.credit`, a nullable `OverLimitCredit`), and
 * `api/openapi.json` is DEV's contract, which syncs once that api deploys. Removable, and to be removed, the moment the
 * snapshot's `OverLimitOffer` carries `credit`; an api without it answers no credit, which reads as none.
 */
/*
 * `planOffers` is exempt too: the sized plan dialog (kitecorp/transferify-ui#3800) reads the list of plans that W2
 * (kitecorp/transferify-api-service#3017) answers, and no deployed api serves it until W2 deploys. Removable, and to be
 * removed, in the schema sync after W2's DEV deploy: the moment the snapshot's `OverLimitOffer` carries `planOffers`.
 */
export type _OverLimitOffer = AssertNoSpecDrift<
	SpecDrift<OverLimitOffer, Schemas["OverLimitOffer"], "credit" | "planOffers">>;
export type _OverLimitAllowance = AssertNoSpecDrift<
	SpecDrift<OverLimitAllowance, Schemas["OverLimitAllowance"]>>;

export class OverLimitApi {
	/**
	 * `GET /transfer/over-limit/offer?bytes=` — what sending this size would cost.
	 *
	 * Answers for ANY size, including ones it will not sell: `required` and `available` carry that, and
	 * the boundaries (`freeCapBytes`, `maxSellableBytes`) ride along so the ui reads both from the
	 * deployed catalogue instead of restating them.
	 */
	static async getOffer(bytes: number, pricing: OverLimitPricing, files: FileListFacts): Promise<OverLimitOffer> {
		const {data} = await axios.get<OverLimitOffer>("/transfer/over-limit/offer", {
			params: {bytes, instant: isInstant(pricing), ...files},
			timeout: READ_TIMEOUT_MS,
		});
		return data;
	}

	/**
	 * `GET /transfer/over-limit/allowance?bytes=` — the settled purchase covering this size, or `null`.
	 *
	 * "None" is a 204. Axios surfaces an empty body as the empty STRING, so a bare `data ?? null` would
	 * return `""` typed as an allowance — a value that passes every `!== null` guard downstream. The STATUS
	 * is what decides here, so an empty body can never masquerade as a purchase.
	 *
	 * Read when the over-cap state arms and on no timer (see {@link ./useOverLimitPurchase}): on a MISS the api
	 * runs its dead-session heal, which retrieves up to five Stripe sessions, so a read must never repeat per tick.
	 */
	static async getAllowance(
		bytes: number, pricing: OverLimitPricing, peerId?: string,
	): Promise<OverLimitAllowance | null> {
		// `peerId` only on an instant send (kitecorp/transferify-api-service#2787): this tab serves only its newest
		// instant send, so its older one that nobody fetched gives its purchase to the send being composed. A hosted
		// send leaves the tab's instant send running, so it never names the peer.
		const params = {bytes, instant: isInstant(pricing),
			...(isInstant(pricing) && peerId !== undefined && peerId !== "" ? {peerId} : {})};
		const {status, data} = await axios.get<OverLimitAllowance | "">(
			"/transfer/over-limit/allowance", {params, timeout: READ_TIMEOUT_MS});
		return status === 204 || data === "" ? null : data;
	}
}

/**
 * The pricing rule as the wire spells it. One translation, used by both calls, so the domain name and the
 * query parameter can never drift apart in one of them.
 */
function isInstant(pricing: OverLimitPricing): boolean {
	return pricing === "instant";
}


/**
 * The pricing rule as the api's own `OverLimitPricing` enum spells it, on the one surface where the api
 * PUBLISHES a rule rather than reading one: each allowance on the unused purchases listing
 * (`GET /account/one-off-purchases`, kitecorp/transferify-api-service#2217). It names the type the
 * allowance's quoted size is a floor on (kitecorp/transferify-api-service#2787, the owner's rule 9 of
 * 2026-10-17: a payment always pays for at least the size it was quoted, on the type it was bought for).
 *
 * One fact has three spellings — the composer's {@link OverLimitPricing} (declared beside the rules in
 * `./overLimitOffer`), the query parameter {@link isInstant} writes, and this enum — and every
 * translation between them lives in THIS file, so no other module can spell the same rule two ways. A
 * listing rule compared against a send's rule goes through {@link overLimitPricingOfRule}: a direct
 * string compare of `"HOSTED"` against `"hosted"` never matches, and a purchase's floor would then never
 * apply to the type it was bought for, with no error anywhere to say so.
 */
export type OverLimitPricingRule = "HOSTED" | "INSTANT";

const PRICING_OF_RULE: Readonly<Record<OverLimitPricingRule, OverLimitPricing>> = {HOSTED: "hosted", INSTANT: "instant"};

/**
 * Whether `value` is a rule the listing may carry. `Object.keys` rather than `in`, which would admit an
 * inherited property name such as `"toString"` as a pricing rule.
 */
export function isOverLimitPricingRule(value: unknown): value is OverLimitPricingRule {
	return typeof value === "string" && Object.keys(PRICING_OF_RULE).includes(value);
}

/** The listing's rule as the composer names it, or null where the listing stated none. */
export function overLimitPricingOfRule(rule: OverLimitPricingRule | null): OverLimitPricing | null {
	return rule === null ? null : PRICING_OF_RULE[rule];
}
